import './ApplianceDetails.scss';

import React, { useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import { AddToCartButtonContainer } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart';
import { Rating } from '@sbi-international/boutik.gatsby.plugin.components';
import DroletButton from '../Primitives/DroletButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IAppliance } from '../../interfaces';
import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { scrollToRef } from '../../utils';
import { useAvailabilityInfo } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart/AddToCartButton';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductPrice } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';
import { useProductReviews } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useProductReviews';
import { useTranslation } from 'react-i18next';

export default function ApplianceDetails(props: {
    appliance: IAppliance;
    reviewsRef: React.MutableRefObject<any>;
    infoRef: React.MutableRefObject<any>;
    openOptions: any;
    specialPriceEnd?: string;
}) {
    const { allProductsJson } = useStaticQuery(
        graphql`
            query {
                allProductsJson(
                    filter: {
                        code: { eq: "drolet_carte-superficie-chauffage" }
                    }
                ) {
                    nodes {
                        title {
                            fr_CA
                            en_CA
                            en_US
                        }
                        documents {
                            fr_CA
                            en_CA
                            en_US
                        }
                    }
                }
            }
        `
    );

    const pdfSuperficie = allProductsJson?.nodes[0];
    const [qtyValue, setQtyValue] = useState(1);
    const { appliance, reviewsRef, infoRef, openOptions, specialPriceEnd } =
        props;
    const { currentLocale } = usePageLocalization();

    const [price] = useProductPrice(appliance.code as string) ?? null;
    const availability = useAvailabilityInfo(appliance)?.availabilityLabel;
    const { t } = useTranslation('layout');

    const promoText =
        appliance?.attributes?.TextPrix?.values[0]?.[currentLocale];

    const productReviews = useProductReviews(appliance.code);

    return (
        <>
            <div className='appliance-details-container'>
                <h1 className='appliance-details-title'>
                    {appliance?.title?.[currentLocale]}
                </h1>
                <span className='appliance-details-code'>{appliance.code}</span>
                {productReviews && productReviews.count != 0 ? (
                    <div className='appliance-details-section rating-section'>
                        <div className='rating'>
                            <Rating.RatingScore
                                ratingScore={productReviews?.average ?? 0}
                                ratingCount={productReviews?.count}
                            />
                            <span
                                className='see-ratings'
                                onClick={() => {
                                    scrollToRef(reviewsRef, 140);
                                }}
                            >
                                {t('appliance.see-reviews')}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className='appliance-details-section rating-section'>
                        <div className='rating'>
                            <span
                                className='see-ratings'
                                style={{ marginLeft: 0 }}
                                onClick={() => {
                                    scrollToRef(reviewsRef, 140);
                                }}
                            >
                                {t('appliance.review-product')}
                            </span>
                        </div>
                    </div>
                )}

                <div className='appliance-details-section price-section'>
                    {promoText && (
                        <span className='promo-text'>{promoText}</span>
                    )}
                    {getProductPrice()}
                </div>
                {specialPriceEnd && (
                    <div className='appliance-details-section price-section'>
                        <span className='promo-text'>
                            {t('appliance.valid-until')} {specialPriceEnd}
                        </span>
                    </div>
                )}
                <div className='appliance-details-section appliance-details-spect'>
                    {renderSpecs(
                        appliance.category.split('/')[3],
                        appliance?.code !== 'DB00400'
                    )}
                </div>
                <div className='appliance-details-section appliance-details-availability'>
                    {availability}
                </div>
                <div className='appliance-details-section add-to-cart-section'>
                    <AddToCartButtonContainer
                        product={appliance}
                        qtyValue={qtyValue}
                        setQtyValue={setQtyValue}
                    />
                </div>
                <div className='appliance-details-section'>
                    <DroletButton
                        label={t('appliance.choose-options')}
                        onClick={() => {
                            infoRef.current.scrollIntoView();
                            openOptions();
                        }}
                    />
                </div>
            </div>
        </>
    );

    function getProductPrice() {
        if (price?.regularPrice.value === 0) {
            return '';
        }
        if (price?.regularPrice.value === price?.finalPrice.value) {
            return (
                <span className='final-price'>
                    {LocalePrice(currentLocale, price?.regularPrice.value)}
                </span>
            );
        } else if (
            price?.regularPrice.value &&
            price?.finalPrice.value &&
            price.regularPrice.value !== price.finalPrice.value
        ) {
            return (
                <>
                    <span className='regular-price'>
                        {LocalePrice(currentLocale, price?.regularPrice.value)}
                    </span>
                    <span className='final-price'>
                        {LocalePrice(currentLocale, price?.finalPrice.value)}
                    </span>
                </>
            );
        }
    }

    function renderSpecs(type: string, showPdf = true) {
        let attributes: string[] = [];
        let displayCarteSuperficie = false;

        switch (type) {
            case 'Wood Stoves':
                displayCarteSuperficie = true;
                attributes = ['Surfi', 'size', 'LongBuci', 'Emiss'];
                break;
            case 'Pellet Stoves':
                displayCarteSuperficie = true;
                attributes = ['Surfi', 'size', 'TempComb'];
                // capacité de trémie ??
                break;
            case 'Camp Stove':
                displayCarteSuperficie = true;
                attributes = ['Surfi', 'size', 'LongBuci', 'Emiss'];
                break;
            case 'Cookstoves':
                attributes = ['size', 'LongBuci', 'Dim_four'];
                break;

            case 'Wood Inserts':
                displayCarteSuperficie = true;
                attributes = ['Surfi', 'size', 'LongBuci', 'Emiss'];
                break;

            case 'Wood Furnaces':
                displayCarteSuperficie = true;
                attributes = ['Surfi', 'LongBuci', 'TempComb'];
                break;

            case 'Outdoor Fireplaces':
                attributes = ['Dim_four', 'LongBuci'];
                break;

            case 'Gas Wall Mounted Room Heater':
                attributes = ['Surfi', 'PWMiEGPi'];
                break;

            default:
                break;
        }

        return (
            <React.Fragment>
                {attributes.map((attr, index) => {
                    if (attr === 'size') {
                        const L =
                            appliance?.attributes?.DimExHLi?.values[0][
                                currentLocale
                            ];
                        const P =
                            appliance?.attributes?.DimExHPi?.values[0][
                                currentLocale
                            ];
                        const H =
                            appliance?.attributes?.DimExHHi?.values[0][
                                currentLocale
                            ];
                        const dimensions =
                            currentLocale === 'fr_CA'
                                ? `${L} L X ${P} P X ${H} H`
                                : `${L} W X ${P} D X ${H} H`;
                        return (
                            <div className='details-spect' key={index}>
                                <span className='spect-title'>
                                    {
                                        appliance.attributes?.[attr]?.name?.[
                                            currentLocale
                                        ]
                                    }
                                </span>
                                <span className='spect-value'>
                                    {dimensions}
                                </span>
                            </div>
                        );
                    } else {
                        return (
                            <div className='details-spect' key={index}>
                                <span className='spect-title'>
                                    {
                                        appliance.attributes?.[attr]?.name?.[
                                            currentLocale
                                        ]
                                    }
                                </span>
                                <span className='spect-value'>
                                    {
                                        appliance.attributes?.[attr]
                                            ?.values[0]?.[currentLocale]
                                    }
                                </span>

                                {showPdf &&
                                    attr === 'Surfi' &&
                                    displayCarteSuperficie && (
                                        <div className='carte-superficie'>
                                            <FontAwesomeIcon
                                                icon={faFileAlt}
                                                className='icon'
                                            />

                                            <a
                                                className='document'
                                                rel='noreferrer'
                                                target='_blank'
                                                href={`/file/${pdfSuperficie?.documents?.[0][currentLocale]}`}
                                            >
                                                {
                                                    pdfSuperficie.title[
                                                        currentLocale
                                                    ]
                                                }
                                            </a>
                                        </div>
                                    )}
                            </div>
                        );
                    }
                })}
            </React.Fragment>
        );
    }
}
